/**
 * Route prefetching utility
 * Preloads primary route chunks in background idle time so section switching is instantaneous
 * without triggering lazy-loading fallbacks or delay.
 */

type RouteLoader = () => Promise<any>;

const ROUTE_LOADERS: Record<string, RouteLoader> = {
  '/': () => import('../pages/Home'),
  '/explore': () => import('../pages/Explore'),
  '/services': () => import('../pages/Services'),
  '/requirements': () => import('../pages/Requirements'),
  '/events': () => import('../pages/Events'),
  '/event-matches-page': () => import('../pages/EventMatchesPage'),
  '/profile': () => import('../pages/Profile'),
  '/messages': () => import('../pages/Messages'),
  '/notifications': () => import('../pages/Notifications'),
  '/create': () => import('../pages/Create'),
};

const prefetchedRoutes = new Set<string>();

/**
 * Preload a specific route by path
 */
export function preloadRoute(path: string): void {
  const normalizedPath = path.split('?')[0].split('#')[0];
  const loader = ROUTE_LOADERS[normalizedPath];
  if (loader && !prefetchedRoutes.has(normalizedPath)) {
    prefetchedRoutes.add(normalizedPath);
    loader().catch(() => {
      // Ignore background prefetch errors (will retry normally if navigated to)
      prefetchedRoutes.delete(normalizedPath);
    });
  }
}

/**
 * Preload all core routes during idle time after app startup
 */
export function prefetchCoreRoutes(): void {
  const routesToPrefetch = [
    '/',
    '/services',
    '/profile',
    '/requirements',
    '/events',
    '/messages',
    '/notifications',
    '/explore',
    '/create',
  ];

  const prefetchQueue = () => {
    let index = 0;
    const loadNext = () => {
      if (index >= routesToPrefetch.length) return;
      const route = routesToPrefetch[index++];
      preloadRoute(route);
      
      // Stagger slightly to avoid saturating CPU / thread
      if (typeof window !== 'undefined') {
        if ('requestIdleCallback' in window) {
          (window as any).requestIdleCallback(loadNext, { timeout: 1000 });
        } else {
          setTimeout(loadNext, 120);
        }
      }
    };

    loadNext();
  };

  if (typeof window !== 'undefined') {
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(prefetchQueue, { timeout: 2000 });
    } else {
      setTimeout(prefetchQueue, 500);
    }
  }
}
